import React, { useMemo } from 'react';
import type { PageContext } from 'vike/types';
import { ReadonlyComponentRenderer } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/ReadonlyComponentRenderer';
import { CustomComponentProvider } from '@mutiny-pkg/bin-ui/src/customComponents/CustomComponentProvider';
import { ThemeProvider } from '@mutiny-pkg/bin-ui/src/rigidMicrosites/theme/ThemeProvider';
import type { ThemeProperties } from '@mutiny-pkg/champaign-schemas/src/schemas/themeProperties/types';
import type { FlexiblePageInput } from '@/pages/types';
import { createCustomComponentAssetUrlResolvers } from './customComponentAssetUrls';
import './componentClasses.css';

type FlexiblePageContentRendererProps = Pick<
  FlexiblePageInput,
  | 'componentTree'
  | 'overrideVariables'
  | 'renderStage'
  | 'staticSiteDomain'
  | 'customComponents'
  | 'isPdfExport'
  | 'isThumbnail'
> & {
  contentStaticAssetsBaseURL?: string | null;
  preloadedCustomComponents?: PageContext['preloadedCustomComponents'];
  sectionOverlay?: React.ReactNode;
  stateConfig?: Parameters<typeof CustomComponentProvider>[0]['stateConfig'];
  skipFontLoading?: boolean;
  afterContent?: React.ReactNode;
  children?: React.ReactNode;
};

export function FlexiblePageContentRenderer({
  componentTree,
  overrideVariables,
  renderStage,
  staticSiteDomain,
  customComponents,
  isPdfExport,
  isThumbnail,
  contentStaticAssetsBaseURL,
  preloadedCustomComponents,
  sectionOverlay,
  stateConfig,
  skipFontLoading,
  afterContent,
  children,
}: FlexiblePageContentRendererProps) {
  const theme = componentTree.data as unknown as ThemeProperties;
  const filesystem = useMemo(
    () => ({
      artifacts: customComponents.artifacts,
      slugToCurrentSourceHash: customComponents.slugToCurrentSourceHash,
    }),
    [customComponents],
  );
  const { resolveCompiledUrl, resolveRuntimeUrl } = useMemo(
    () =>
      createCustomComponentAssetUrlResolvers({
        renderStage,
        staticSiteDomain,
        getPageOrigin: () => window.location.origin,
      }),
    [renderStage, staticSiteDomain],
  );

  return (
    <ThemeProvider theme={theme}>
      <CustomComponentProvider
        filesystem={filesystem}
        theme={theme as unknown as Parameters<typeof CustomComponentProvider>[0]['theme']}
        overrideVariables={overrideVariables}
        isEditor={false}
        resolveCompiledUrl={resolveCompiledUrl}
        resolveRuntimeUrl={resolveRuntimeUrl}
        preloadedModules={preloadedCustomComponents}
        stateConfig={stateConfig}
      >
        <ReadonlyComponentRenderer
          componentTree={componentTree}
          overrideVariables={overrideVariables}
          staticAssetsBaseURL={contentStaticAssetsBaseURL ?? staticSiteDomain}
          renderStage={renderStage}
          isPdfExport={isPdfExport}
          isThumbnail={isThumbnail}
          sectionOverlay={sectionOverlay}
          skipFontLoading={skipFontLoading}
        >
          {children}
        </ReadonlyComponentRenderer>
        {afterContent}
      </CustomComponentProvider>
    </ThemeProvider>
  );
}
